import { useState, useEffect, Fragment } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Collapse from "@mui/material/Collapse";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { formatTimestamp } from "./formatters";
import { LogEntry, LogMetadata } from "./types";
import { tokens } from "./tokens";
import LogDetailContent from "./LogDetailContent";

interface LogDetailModalProps {
  // The occurrences to list; the modal is open whenever this is non-null.
  logs: LogEntry[] | null;
  // Heading text (the shared/cleaned message for the group).
  title?: string;
  onClose: () => void;
}

// Real columns, in order. A trailing spacer column (rendered separately) soaks up
// the leftover width so these stay content-sized and packed to the left.
const HEADERS = ["Timestamp", "Message", "Error Code", "Tags"];
const COL_SPAN = HEADERS.length + 1; // + spacer, for the full-width detail row

// Shrink-to-content: width:1% in a 100%-wide table collapses the cell to its
// content's min width; nowrap stops it wrapping.
const shrink = { width: "1%", whiteSpace: "nowrap" as const };

const headSx = {
  fontWeight: 700,
  fontSize: "0.72rem",
  color: tokens.textSecondary,
  whiteSpace: "nowrap" as const,
};

export default function LogDetailModal({ logs, title, onClose }: LogDetailModalProps) {
  // Index of the currently expanded row; only one open at a time.
  const [expanded, setExpanded] = useState<number | null>(null);

  // Reset the open row whenever a different group is opened.
  useEffect(() => {
    setExpanded(null);
  }, [logs]);

  const open = !!logs;
  const rows = logs || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6, py: 1.5 }}>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
          {title || "Log details"}
        </Typography>
        <Typography
          component="span"
          sx={{ ml: 1, color: tokens.textSecondary, fontSize: "0.82rem" }}
        >
          ({rows.length} {rows.length === 1 ? "occurrence" : "occurrences"})
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1.5 }}>
        <Table
          size="small"
          sx={{
            width: "100%",
            tableLayout: "auto",
            "& .MuiTableCell-root": {
              borderColor: tokens.divider,
              py: 0.5,
              px: 1,
              fontSize: "0.8rem",
              verticalAlign: "top",
            },
          }}
        >
          <TableHead>
            <TableRow sx={{ "& .MuiTableCell-root": { bgcolor: tokens.elevated } }}>
              <TableCell sx={{ ...headSx, ...shrink }}>Timestamp</TableCell>
              <TableCell sx={headSx}>Message</TableCell>
              <TableCell sx={{ ...headSx, ...shrink }}>Error Code</TableCell>
              <TableCell sx={{ ...headSx, ...shrink }}>Tags</TableCell>
              {/* spacer: eats remaining width so real columns stay packed left */}
              <TableCell sx={headSx} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((log, i) => {
              const meta: LogMetadata = log.metadata || {};
              const errCode = (meta.statusCode || meta.errorCode || "") as string | number;
              const tags = log.filteredTags || [];
              const isOpen = expanded === i;
              return (
                <Fragment key={log.id || i}>
                  <TableRow
                    hover
                    onClick={() => setExpanded(isOpen ? null : i)}
                    sx={{
                      cursor: "pointer",
                      "& > *": { borderBottom: isOpen ? "unset" : undefined },
                    }}
                  >
                    <TableCell
                      sx={{ ...shrink, fontFamily: tokens.mono, color: tokens.textSecondary }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <KeyboardArrowRightIcon
                          fontSize="small"
                          sx={{
                            fontSize: "1rem",
                            color: tokens.textTertiary,
                            transform: isOpen ? "rotate(90deg)" : "none",
                            transition: "transform 0.15s",
                          }}
                        />
                        {formatTimestamp(log.timestamp) || "—"}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>
                      <Tooltip title={log.message || ""} placement="top-start">
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ fontSize: "0.8rem", color: tokens.textPrimary }}
                        >
                          {log.message || "—"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ ...shrink, fontFamily: tokens.mono }}>
                      {errCode || "—"}
                    </TableCell>
                    <TableCell sx={shrink}>
                      {tags.length > 0 ? (
                        <Box sx={{ display: "flex", gap: 0.4, flexWrap: "wrap" }}>
                          {tags.map((t, idx) => (
                            <Chip
                              key={idx}
                              label={t}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                fontSize: "0.68rem",
                                borderColor: tokens.divider,
                                color: tokens.textSecondary,
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    {/* spacer */}
                    <TableCell />
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={COL_SPAN}
                      sx={{ p: 0, borderBottom: isOpen ? undefined : "none" }}
                    >
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: tokens.surface }}>
                          <LogDetailContent log={log} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COL_SPAN} align="center" sx={{ py: 3, color: tokens.textSecondary }}>
                  No occurrences.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}
